// Renders the admin-managed header/body/footer script slots.
//
// Scope requirement: a controlled area where authorised administrators can
// insert tracking or third-party scripts (Google Analytics, GTM, Meta Pixel).
// Editing these is restricted to superadmins in firestore.rules, because
// anything placed here executes on every page for every visitor.
//
// React will not execute a <script> tag inserted via innerHTML, so the markup
// is parsed and each node re-created through the DOM API, which does execute.
import { useEffect } from "react";
import { useCodeInjection } from "@/hooks/content";

const MARKER = "data-sky-injected";

/**
 * Parse an HTML fragment and append its nodes to `target`.
 * Returns the appended nodes so they can be removed on cleanup.
 */
function injectInto(target, html, slot) {
  if (!html || !html.trim()) return [];

  const template = document.createElement("template");
  template.innerHTML = html;

  const appended = [];

  Array.from(template.content.childNodes).forEach((node) => {
    let toAppend = node;

    // A <script> cloned from a template is inert; recreate it so the browser
    // executes it.
    if (node.tagName === "SCRIPT") {
      const script = document.createElement("script");
      Array.from(node.attributes).forEach((attr) =>
        script.setAttribute(attr.name, attr.value)
      );
      script.text = node.text || "";
      toAppend = script;
    }

    if (toAppend.setAttribute) {
      toAppend.setAttribute(MARKER, slot);
    }

    target.appendChild(toAppend);
    appended.push(toAppend);
  });

  return appended;
}

export default function CodeInjection() {
  const { data } = useCodeInjection();

  const header = data?.header || "";
  const body = data?.body || "";
  const footer = data?.footer || "";

  useEffect(() => {
    // Clear anything from a previous render before re-injecting, so saving in
    // the admin panel does not stack duplicate tags during a session.
    document
      .querySelectorAll(`[${MARKER}]`)
      .forEach((el) => el.parentNode?.removeChild(el));

    const nodes = [
      ...injectInto(document.head, header, "header"),
      // "body" scripts conventionally go immediately after <body> opens.
      ...injectInto(document.body, body, "body"),
      ...injectInto(document.body, footer, "footer"),
    ];

    return () => {
      nodes.forEach((node) => node.parentNode?.removeChild(node));
    };
  }, [header, body, footer]);

  return null;
}
